import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Repeat, Pause, Play, Square, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, Empty } from "@/components/common";
import { AssigneePicker } from "@/components/OrderFormDialog";
import { RecurrenceFields, recFrom, recPayload, durationLabel } from "@/components/RecurrenceFields";
import { WorkTypeChecks, applyWorkTypes } from "@/components/WorkTypeChecks";
import { fmtDT } from "@/lib/format";
import { cn } from "@/lib/utils";

const ST = { attiva: ["Attiva", "bg-emerald-100 text-emerald-800"], pausa: ["In pausa", "bg-amber-100 text-amber-800"], terminata: ["Terminata", "bg-zinc-200 text-zinc-700"] };

const EditDialog = ({ r, commesse, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (r) {
      const t = r.template;
      setF({ ...t, work_types: t.work_types || [], cimitero_id: t.cimitero_id || "", checklist: t.checklist.join("\n"), required_qualifications: t.required_qualifications.join(", ") });
      setRec(recFrom(r));
    }
  }, [r]);
  if (!r || !f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const cims = commesse.find((c) => c.id === f.commessa_id)?.cimiteri || [];
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, cimitero_id: f.cimitero_id || null, checklist: f.checklist.split("\n").map((s) => s.trim()).filter(Boolean),
        required_qualifications: f.required_qualifications.split(",").map((s) => s.trim()).filter(Boolean), ...recPayload(rec) };
      const { data } = await api.put(`/recurrences/${r.id}`, body);
      toast.success(`Ricorrenza aggiornata · ${data.orders_updated} ordini aggiornati${data.orders_cancelled ? ` · ${data.orders_cancelled} annullati` : ""}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="recurrence-edit-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">Modifica ordine ricorrente</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Le modifiche valgono per i prossimi ordini e aggiornano anche quelli già creati e non ancora iniziati.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Titolo *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="rec-edit-title" /></div>
          <WorkTypeChecks value={f.work_types} onChange={(v) => setF((x) => applyWorkTypes(x, v))} testid="rec-edit-work-type" />
          <div className="space-y-1.5 sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Cimitero</Label><NativeSelect className="w-full" testid="rec-edit-cimitero" value={f.cimitero_id} onChange={(v) => set("cimitero_id", v)}><option value="">—</option>{cims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Assegnatari</Label><AssigneePicker commessaId={f.commessa_id} value={f.assignee_ids} onChange={(v) => set("assignee_ids", v)} testid="rec-edit-assignees" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Checklist (una voce per riga)</Label><Textarea value={f.checklist} onChange={(e) => set("checklist", e.target.value)} data-testid="rec-edit-checklist" /></div>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm font-medium">Richiede verifica del responsabile<Switch checked={f.requires_verification} onCheckedChange={(v) => set("requires_verification", v)} /></label>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm font-medium">Foto prima/dopo obbligatorie<Switch checked={f.requires_photos} onCheckedChange={(v) => set("requires_photos", v)} /></label>
          <RecurrenceFields value={rec} onChange={setRec} />
        </div>
        <DialogFooter><Button disabled={busy || !f.title || !rec.weekdays.length} onClick={save} className="h-11 rounded-xl font-bold" data-testid="rec-edit-submit">Salva modifiche</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecCard = ({ r, canEdit, onEdit, onStatus }) => {
  const [label, cls] = ST[r.status];
  return (
    <div className="card-soft p-5" data-testid={`recurrence-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="icon-chip h-10 w-10 shrink-0"><Repeat className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-bold" data-testid={`recurrence-title-${r.id}`}>{r.template.title}</p>
          <p className="text-xs text-muted-foreground">{r.commessa_code}{r.cimitero_name ? ` · ${r.cimitero_name}` : ""} · {r.assignee_names.join(", ") || "da assegnare"}</p>
        </div>
        <span className={cn("rounded-full px-3 py-1 text-xs font-bold", cls)} data-testid={`recurrence-status-${r.id}`}>{label}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div><p className="text-xs uppercase text-muted-foreground">Giorni</p><p className="font-semibold capitalize">{r.days_label}</p></div>
        <div><p className="text-xs uppercase text-muted-foreground">Orario</p><p className="font-semibold">{r.start_time}–{r.end_time}</p></div>
        <div><p className="text-xs uppercase text-muted-foreground">Durata</p><p className="font-semibold">{durationLabel(r)}</p></div>
        <div><p className="text-xs uppercase text-muted-foreground">Prossimo</p><p className="font-semibold" data-testid={`recurrence-next-${r.id}`}>{r.next_occurrence ? fmtDT(r.next_occurrence) : "—"}</p></div>
      </div>
      {r.holds?.length > 0 && <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`recurrence-holds-${r.id}`}>
        {r.holds.map((h) => <p key={h.order_id}>Sospeso <b>{h.user_name}</b> fino alla fine di <Link to={`/ordini/${h.order_id}`} className="font-bold underline">{h.order_code}</Link></p>)}
      </div>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link to={`/ordini?recurrence_id=${r.id}`} className="mr-auto text-sm font-semibold text-primary" data-testid={`recurrence-orders-${r.id}`}>{r.generated} ordini creati</Link>
        {canEdit && r.status !== "terminata" && <>
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => onEdit(r)} data-testid={`recurrence-edit-${r.id}`}><Pencil className="mr-1 h-3 w-3" />Modifica</Button>
          {r.status === "attiva"
            ? <Button size="sm" variant="outline" className="rounded-xl" onClick={() => onStatus(r, "pausa")} data-testid={`recurrence-pause-${r.id}`}><Pause className="mr-1 h-3 w-3" />Pausa</Button>
            : <Button size="sm" variant="outline" className="rounded-xl" onClick={() => onStatus(r, "attiva")} data-testid={`recurrence-resume-${r.id}`}><Play className="mr-1 h-3 w-3" />Riprendi</Button>}
          <Button size="sm" variant="outline" className="rounded-xl text-red-600" onClick={() => onStatus(r, "terminata")} data-testid={`recurrence-stop-${r.id}`}><Square className="mr-1 h-3 w-3" />Termina</Button>
        </>}
      </div>
    </div>
  );
};

export default function Ricorrenze() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [cid, setCid] = useState("");
  const [edit, setEdit] = useState(null);
  const [stop, setStop] = useState(null);
  const canEdit = ["ceo", "responsabile"].includes(user.role);
  const load = useCallback(() => {
    api.get("/recurrences", { params: cid ? { commessa_id: cid } : {} }).then((r) => setRows(r.data));
  }, [cid]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
  }, []);
  const setStatus = async (r, status) => {
    try {
      const { data } = await api.post(`/recurrences/${r.id}/status`, { status });
      toast.success(status === "terminata" ? `Ricorrenza terminata${data.orders_cancelled ? ` · ${data.orders_cancelled} ordini futuri annullati` : ""}` : status === "pausa" ? "Ricorrenza in pausa" : "Ricorrenza riattivata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const onStatus = (r, s) => (s === "terminata" ? setStop(r) : setStatus(r, s));
  return (
    <div data-testid="ricorrenze-page">
      <PageHeader title="Ordini ricorrenti" subtitle={rows ? `${rows.length} ricorrenze` : "…"}
        actions={<NativeSelect testid="ricorrenze-filter-commessa" value={cid} onChange={setCid}><option value="">Tutte le commesse</option>{commesse.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}</NativeSelect>} />
      {rows && rows.length === 0 && <Empty testid="ricorrenze-empty" text="Nessun ordine ricorrente. Crealo da «Nuovo ordine» attivando «Ordine ricorrente»." />}
      <div className="grid gap-4 xl:grid-cols-2">{rows?.map((r) => <RecCard key={r.id} r={r} canEdit={canEdit} onEdit={setEdit} onStatus={onStatus} />)}</div>
      <EditDialog r={edit} commesse={commesse} onClose={() => setEdit(null)} onSaved={load} />
      <AlertDialog open={!!stop} onOpenChange={(o) => !o && setStop(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="recurrence-stop-dialog">
          <AlertDialogHeader><AlertDialogTitle>Terminare la ricorrenza?</AlertDialogTitle>
            <AlertDialogDescription>Non verranno più creati ordini. Gli ordini futuri già creati e non ancora iniziati saranno annullati. L'operazione non si può annullare.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Indietro</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-red-600 hover:bg-red-700" onClick={() => { setStatus(stop, "terminata"); setStop(null); }} data-testid="recurrence-stop-confirm">Termina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
